import React, { useContext, useRef } from 'react';
import styled, { css } from 'styled-components';
import { Variable } from 'src/api/types';
import _ from 'lodash';
import { useStateSelector } from 'src/redux';
import { ColorSet } from 'src/redux/reducers/theme';
import { formatNumber } from 'src/utils/misc';
import { isWorkspaceOrGlobalVariable } from '../util';
import { GradientText, InputLabel, Poppins } from 'src/common';
import { SuggestionEditingContext } from '../FormulaInput';
import { CurrencyInputFormula } from 'src/components/form/CurrencyInput';
import Input from 'src/components/form/Input';
import {
  IconVariableWorkspace,
  IconVariableAssessment,
  IconVariableWorkspaceCustom,
  IconVariableAssessmentCustom,
} from 'src/assets/dynamicSvg';

const Div = styled.div<{ $isEditing?: boolean; $colors: ColorSet }>`
  ${({ $colors, $isEditing }) =>
    $isEditing &&
    css`
      border-radius: 6px;
      outline: 1px solid ${$colors.aliceBlue};
      background: ${$colors.bgGray};
      margin-bottom: 4px;
    `};

  .se-editing {
    padding: 0 8px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 14px;
    margin-top: 4px;

    .se-editing__button {
    }
    .se-editing__cancel {
      color: ${({ $colors }) => $colors.prussianBlue};
    }
  }

  .se-content {
    display: grid;
    grid-template-columns: 12px auto auto;
    grid-gap: 6px;
    border-radius: 4px;
    font-weight: 500;
    padding: 4px 8px;

    .se-item {
      align-self: center;

      &__center {
        align-self: center;
        justify-self: center;
      }
      &__name {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 350px;
        margin-right: 10px;
        color: ${({ $colors }) => $colors.davysGrey};
      }
      &__end {
        justify-self: end;
        color: ${({ $colors }) => $colors.prussianBlue};

        &__controls {
          display: flex;
          align-items: center;
          gap: 12px;
        }
      }
    }

    .se-add-value {
      &:hover {
        text-decoration: underline;
      }
    }

    &:hover {
      cursor: pointer;
    }

    &:active,
    &:hover,
    &:focus,
    &:visited,
    &.myz2dw1 {
      background: ${({ $colors }) => $colors.gradient};
      color: ${({ $colors }) => $colors.white};
      .se-item {
        &__name {
          color: ${({ $colors }) => $colors.white};
        }
        &__end {
          color: ${({ $colors }) => $colors.white};
        }
      }
      .se-add-value {
        color: ${({ $colors }) => $colors.white};
      }
      path {
        fill: ${({ $colors }) => $colors.white};
      }
    }

    ${({ $colors, $isEditing }) =>
      $isEditing &&
      css`
        border: none;
        background: transparent;

        &:hover,
        &:active,
        &:focus,
        &.myz2dw1 {
          background: transparent;
          .se-item {
            &__name {
              color: ${$colors.davysGrey};
            }
            &__end {
              color: ${$colors.cflowerBlue};
            }
          }
          .se-add-value {
            color: ${$colors.gp2};
          }
          .wizard-icon path {
            fill: url(#paint0_linear_1022_1157\\}) !important;
          }
        }
      `}
  }
`;

const EditButton = styled.button<{ $disabled?: boolean; $variant?: 'gradient' | 'default'; $colors: ColorSet }>`
  background: none;
  border: none;
  padding: 0;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 2px;
    height: 1px;
    background: ${({ $colors, $variant }) =>
      $variant === 'gradient' ? $colors.gradient : $colors.prussianBlue};
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  &:hover::after {
    opacity: ${({ $disabled }) => ($disabled ? 0 : 1)};
  }
`;

interface SuggestionEntryProps {
  mention: Variable;
}

export const SuggestionEntryComponent: React.FC<SuggestionEntryProps> = ({ mention, ...rest }) => {
  const { onClick: parentOnClick, ...restWithoutClick } = rest as any;
  const colors = useStateSelector(({ theme }) => theme.colors);
  const {
    editingVariableId,
    setEditingVariableId,
    cancelEditing,
    onSaveVariableValue,
    isSaving,
    setIsSaving,
    editingInputValue,
    setEditingInputValue,
    editingCurrency,
    setEditingCurrency,
    insertVariableToFormula,
    workspaceVariableNames,
  } = useContext(SuggestionEditingContext);

  const isEditing = editingVariableId === mention.id;
  const inputValue = isEditing ? editingInputValue : undefined;
  const setInputValue = setEditingInputValue;
  const isCurrency = isEditing ? editingCurrency : mention.currency;
  const setIsCurrency = setEditingCurrency;

  const hasNoValue = _.isNil(mention.value);
  const containerRef = useRef<HTMLDivElement>(null);

  const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation();

  const handleAddValueClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingInputValue(undefined);
    setEditingCurrency(mention.currency);
    setEditingVariableId(mention.id);
  };

  const handleRootClick = (e: React.MouseEvent) => {
    if (isEditing) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    parentOnClick?.(e);
  };

  const getVariableIcon = () => {
    const isWorkspaceVar = isWorkspaceOrGlobalVariable(mention);
    const isAssessmentVar = !isWorkspaceVar;

    const isOverride = isAssessmentVar && workspaceVariableNames?.has(mention.name);

    if (isOverride) {
      return <IconVariableAssessmentCustom />;
    }

    if (mention.isGlobal) {
      return <IconVariableWorkspace />;
    }

    if (isWorkspaceVar) {
      return <IconVariableWorkspaceCustom />;
    }

    return <IconVariableAssessment />;
  };

  const saveDisabled = isSaving || inputValue === undefined;

  return (
    <Div {...restWithoutClick} $isEditing={isEditing} $colors={colors} ref={containerRef} onClick={handleRootClick}>
      <div className="se-content">
        <div className="se-item se-item__center">{getVariableIcon()}</div>
        <div className="se-item se-item__name">
          <span style={{ whiteSpace: 'pre' }}>{mention.alias || mention.name}</span>
        </div>
        {!hasNoValue ? (
          <div className="se-item se-item__end">
            {formatNumber(mention.value, { symbol: !!mention.currency, maxDecimals: 3 })}
          </div>
        ) : (
          <div
            className="se-item se-item__end"
            onClick={stopPropagation}
            onMouseDown={stopPropagation}
            onKeyDown={stopPropagation}
            onKeyUp={stopPropagation}
            onKeyPress={stopPropagation}
          >
            {isEditing ? (
              <div className="se-item__end__controls">
                <InputLabel>CURRENCY</InputLabel>
                <div
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    console.log('Switch clicked, current:', isCurrency, '-> new:', !isCurrency);
                    setIsCurrency(!isCurrency);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <Input
                    checked={isCurrency}
                    onChange={() => {}}
                    className="switch switch--small"
                    type="checkbox"
                    style={{ pointerEvents: 'none' }}
                  />
                </div>
                <CurrencyInputFormula
                  autoFocus
                  currency={isCurrency}
                  disabled={isSaving}
                  value={inputValue ?? ''}
                  onValueChange={({ floatValue }) => {
                    setInputValue(floatValue);
                  }}
                />
              </div>
            ) : (
              <span
                onMouseDownCapture={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  handleAddValueClick(e);
                }}
                onClickCapture={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                }}
              >
                <GradientText className="se-add-value" px={14} weight={500} $noTransform>
                  + Add Value
                </GradientText>
              </span>
            )}
          </div>
        )}
      </div>
      {isEditing && (
        <div
          className="se-editing"
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <EditButton
            type="button"
            $disabled={isSaving}
            $variant="default"
            $colors={colors}
            disabled={isSaving}
            onMouseDown={(e) => {
              if (isSaving) return;
              e.preventDefault();
              e.stopPropagation();
              setInputValue(undefined);
              cancelEditing();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Poppins className="se-editing__cancel se-editing__button" px={14} weight={500}>
              Cancel
            </Poppins>
          </EditButton>

          <EditButton
            type="button"
            $disabled={saveDisabled}
            $variant="gradient"
            $colors={colors}
            disabled={saveDisabled}
            onMouseDown={async (e) => {
              if (saveDisabled) return;
              e.preventDefault();
              e.stopPropagation();
              if (inputValue !== undefined && onSaveVariableValue) {
                setIsSaving(true);
                try {
                  await onSaveVariableValue({ variableId: mention.id, value: inputValue, currency: isCurrency });
                  const updatedVariable = { ...mention, value: inputValue, currency: isCurrency };
                  insertVariableToFormula(updatedVariable);
                } finally {
                  setIsSaving(false);
                }
              } else {
                cancelEditing();
              }
            }}
          >
            <GradientText className="se-editing__save se-editing__button" px={14} weight={500} $noTransform>
              Save
            </GradientText>
          </EditButton>
        </div>
      )}
    </Div>
  );
};
